import { useEffect, useRef } from 'react';

// https://medium.com/trabe/preventing-click-events-on-double-click-with-react-the-performant-way-1416ab03b835

type ClickFunction = (e: React.MouseEvent<SVGRectElement, MouseEvent>) => void;

const noop = () => {};

const requestTimeout = (
  fn: () => void,
  delay: number,
  registerCancel: (fn: () => void) => void
) => {
  const start = new Date().getTime();

  const loop = () => {
    const delta = new Date().getTime() - start;

    if (delta >= delay) {
      fn();
      registerCancel(noop);
      return;
    }

    const raf = requestAnimationFrame(loop);
    registerCancel(() => cancelAnimationFrame(raf));
  };

  const raf = requestAnimationFrame(loop);
  registerCancel(() => cancelAnimationFrame(raf));
};

const useCancelableScheduledWork = () => {
  const cancelCallback = useRef<() => void>(noop);

  const registerCancel = (fn: () => void) => {
    cancelCallback.current = fn;
  };

  const cancelScheduledWork = () => cancelCallback.current();

  // Cancels the current sheduled work before the "unmount"
  useEffect(() => {
    return cancelScheduledWork;
  }, []);

  return [registerCancel, cancelScheduledWork] as const;
};

type UseDoubleClickPreventionOptions = {
  onClick: ClickFunction;
  onDoubleClick: ClickFunction;
  delay?: number;
};

export const useDoubleClickPrevention = ({
  onClick,
  onDoubleClick,
  delay = 300,
}: UseDoubleClickPreventionOptions) => {
  const [registerCancel, cancelScheduledRaf] = useCancelableScheduledWork();

  const handleClick: React.MouseEventHandler<SVGRectElement> = e => {
    cancelScheduledRaf();
    requestTimeout(() => onClick(e), delay, registerCancel);
  };

  const handleDoubleClick: React.MouseEventHandler<SVGRectElement> = e => {
    cancelScheduledRaf();
    onDoubleClick(e);
  };

  return [handleClick, handleDoubleClick];
};
